import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SparklesIcon, SearchIcon, Loader2Icon, ChevronRightIcon, ChevronLeftIcon, XIcon, FileSpreadsheetIcon, ArrowRightIcon, ArrowLeftIcon, PenLineIcon, FolderIcon, CheckCircle2Icon, ChevronDownIcon, PlusIcon, SettingsIcon, ClockIcon, SendIcon, LightbulbIcon, DatabaseIcon, FileIcon, TargetIcon, UsersIcon, MailIcon, PhoneIcon, ExternalLinkIcon, DownloadIcon, PencilIcon, ThumbsUpIcon, ThumbsDownIcon, CopyIcon } from 'lucide-react';
const STEP_DURATION = 15000;
const steps = [{
  id: 1,
  title: "Describe what you're looking for",
  description: 'Type a natural language query describing your ideal customer. No complex filters needed.'
}, {
  id: 2,
  title: 'Answer a few quick questions',
  description: 'The agent asks clarifying questions about revenue, company size, and funding stage to refine your search.'
}, {
  id: 3,
  title: 'Review scored companies',
  description: 'Each lead is scored across 4 dimensions with weighted priorities so your highest-value leads surface first.'
}, {
  id: 4,
  title: 'Get decision-maker contacts',
  description: 'Access verified owner and manager-level contacts with direct emails and phone numbers.'
}, {
  id: 5,
  title: 'Send personalized outreach',
  description: 'AI-drafted emails reference specific data points about each lead for maximum relevance.'
}];
function ChatWindow({
  children,
  inputPlaceholder = 'Ask a follow-up question...',
  inputValue = ''
}: {
  children: React.ReactNode;
  inputPlaceholder?: string;
  inputValue?: string;
}) {
  return <div className="bg-slate-50 rounded-2xl border border-slate-200 shadow-xl overflow-hidden flex flex-col h-[480px] w-full max-w-2xl mx-auto" data-id="element-2818">
      <div className="px-4 py-3 border-b border-stone-200/50 flex items-center justify-between bg-white/50" data-id="element-2819">
        <div className="flex items-center gap-2" data-id="element-2820">
          <span className="text-sm font-semibold text-stone-800" data-id="element-2821">
            Sales lead list - retail SMBs
          </span>
          <ChevronRightIcon className="w-4 h-4 text-stone-400 rotate-90" data-id="element-2822" />
        </div>
      </div>
      <div className="p-5 space-y-6 flex-1 overflow-y-auto" data-id="element-2823">{children}</div>
      <div className="p-4 border-t border-stone-200/50 bg-white/50" data-id="element-2824">
        <div className="relative" data-id="element-2825">
          <input type="text" placeholder={inputPlaceholder} value={inputValue} className="w-full pl-4 pr-12 py-3 rounded-xl border border-stone-200 bg-white text-sm focus:outline-none placeholder:text-stone-400" readOnly data-id="element-2826" />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-stone-100 rounded-lg flex items-center justify-center" data-id="element-2827">
            <ArrowLeftIcon className="w-4 h-4 text-stone-400 rotate-90" data-id="element-2828" />
          </div>
        </div>
      </div>
    </div>;
}
function UserMessage({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="flex justify-end" data-id="element-2829">
      <div className="bg-stone-800 text-white rounded-2xl rounded-tr-sm px-5 py-3.5 text-sm max-w-[85%] leading-relaxed shadow-sm" data-id="element-2830">
        {children}
      </div>
    </div>;
}
function AgentMessage({
  children,
  actionLine
}: {
  children: React.ReactNode;
  actionLine?: string;
}) {
  return <div className="space-y-3" data-id="element-2831">
      {actionLine && <div className="flex items-center gap-2 text-xs text-stone-500 pl-1" data-id="element-2832">
          <ChevronRightIcon className="w-3.5 h-3.5" data-id="element-2833" />
          {actionLine}
        </div>}
      <div className="flex items-start gap-3" data-id="element-2834">
        <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center shrink-0 mt-0.5" data-id="element-2835">
          <SparklesIcon className="w-3.5 h-3.5 text-blue-600" data-id="element-2836" />
        </div>
        <div className="flex-1 space-y-3" data-id="element-2837">
          {children}
          <div className="flex items-center gap-2 pt-2" data-id="element-2838">
            <button className="p-1.5 text-stone-400 hover:text-stone-600 rounded-md hover:bg-stone-100 transition-colors" data-id="element-2839">
              <CopyIcon className="w-3.5 h-3.5" data-id="element-2840" />
            </button>
            <button className="p-1.5 text-stone-400 hover:text-stone-600 rounded-md hover:bg-stone-100 transition-colors" data-id="element-2841">
              <ThumbsUpIcon className="w-3.5 h-3.5" data-id="element-2842" />
            </button>
            <button className="p-1.5 text-stone-400 hover:text-stone-600 rounded-md hover:bg-stone-100 transition-colors" data-id="element-2843">
              <ThumbsDownIcon className="w-3.5 h-3.5" data-id="element-2844" />
            </button>
          </div>
        </div>
      </div>
    </div>;
}
function Step1Illustration() {
  return <ChatWindow inputValue="Find hair salons in San Francisco with 2+ locations|" data-id="element-2845">
      <div className="h-full flex items-center justify-center flex-col text-center space-y-4 opacity-50" data-id="element-2846">
        <div className="w-12 h-12 bg-white rounded-2xl shadow-sm flex items-center justify-center border border-stone-200" data-id="element-2847">
          <SparklesIcon className="w-6 h-6 text-blue-600" data-id="element-2848" />
        </div>
        <div data-id="element-2849">
          <h3 className="text-sm font-semibold text-stone-800" data-id="element-2850">
            How can I help you prospect today?
          </h3>
          <p className="text-xs text-stone-500 mt-1" data-id="element-2851">
            Describe your ideal customer profile
          </p>
        </div>
      </div>
    </ChatWindow>;
}
function Step2Illustration() {
  return <ChatWindow data-id="element-2852">
      <UserMessage data-id="element-2853">
        Find hair salons in San Francisco with 2+ locations
      </UserMessage>
      <AgentMessage actionLine="Analyzing query & checking database..." data-id="element-2854">
        <p className="text-sm text-stone-800 leading-relaxed" data-id="element-2855">
          I can help with that. To refine your search and find the best matches,
          I have a quick question:
        </p>
        <div className="bg-white rounded-xl border border-stone-200 p-4 shadow-sm mt-3" data-id="element-2856">
          <p className="text-sm font-semibold text-stone-800 mb-3" data-id="element-2857">
            What's their estimated current revenue?
          </p>
          <div className="space-y-2" data-id="element-2858">
            {['Under $1M', '$1M–$10M', '$10M–$50M'].map((opt, i) => <div key={i} className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${i === 1 ? 'bg-blue-50 border-blue-200' : 'bg-white border-stone-200 hover:bg-stone-50'}`} data-id="element-2859">
                <div className={`w-5 h-5 rounded-md flex items-center justify-center text-[10px] font-bold ${i === 1 ? 'bg-blue-600 text-white' : 'bg-stone-100 text-stone-500'}`} data-id="element-2860">
                  {i + 1}
                </div>
                <span className={`text-sm ${i === 1 ? 'text-blue-900 font-medium' : 'text-stone-600'}`} data-id="element-2861">
                  {opt}
                </span>
              </div>)}
          </div>
        </div>
      </AgentMessage>
    </ChatWindow>;
}
function Step3Illustration() {
  return <ChatWindow data-id="element-2862">
      <UserMessage data-id="element-2863">
        Find hair salons in San Francisco with 2+ locations. Revenue: $1M–$10M.
      </UserMessage>
      <AgentMessage actionLine="Ran 3 commands, scored 28 companies >" data-id="element-2864">
        <p className="text-sm text-stone-800 leading-relaxed" data-id="element-2865">
          All done! I found <strong data-id="element-2866">28 qualified leads</strong> matching your
          criteria. Here are the top results scored by revenue potential and
          intent:
        </p>
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden mt-3" data-id="element-2867">
          <div className="overflow-x-auto" data-id="element-2868">
            <table className="w-full text-sm text-left" data-id="element-2869">
              <thead data-id="element-2870">
                <tr className="border-b border-stone-100 bg-stone-50/50" data-id="element-2871">
                  <th className="px-4 py-2.5 font-semibold text-stone-500" data-id="element-2872">
                    Company
                  </th>
                  <th className="px-4 py-2.5 font-semibold text-stone-500" data-id="element-2873">
                    Domain
                  </th>
                  <th className="px-4 py-2.5 font-semibold text-stone-500" data-id="element-2874">
                    Size
                  </th>
                  <th className="px-4 py-2.5 font-semibold text-stone-500 text-right" data-id="element-2875">
                    Score
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100" data-id="element-2876">
                {[{
                name: 'Supercuts',
                domain: 'supercuts.com',
                size: '50–200',
                score: 94
              }, {
                name: 'Sephora',
                domain: 'sephora.com',
                size: '200+',
                score: 91
              }, {
                name: 'SmartStyle',
                domain: 'smartstyle.com',
                size: '50–200',
                score: 87
              }].map((lead, i) => <tr key={i} className="hover:bg-stone-50/50 transition-colors" data-id="element-2877">
                    <td className="px-4 py-2.5 font-medium text-stone-800" data-id="element-2878">
                      {lead.name}
                    </td>
                    <td className="px-4 py-2.5 text-stone-500" data-id="element-2879">
                      {lead.domain}
                    </td>
                    <td className="px-4 py-2.5 text-stone-500" data-id="element-2880">{lead.size}</td>
                    <td className="px-4 py-2.5 text-right" data-id="element-2881">
                      <span className={`inline-flex items-center justify-center px-2.5 py-1 rounded-lg text-xs font-bold ${lead.score >= 90 ? 'bg-blue-100 text-blue-700' : 'bg-blue-50 text-blue-600'}`} data-id="element-2882">
                        {lead.score}
                      </span>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </AgentMessage>
    </ChatWindow>;
}
function Step4Illustration() {
  return <ChatWindow data-id="element-2883">
      <UserMessage data-id="element-2884">Get decision-maker contacts for the top leads.</UserMessage>
      <AgentMessage actionLine="Enriched 28 companies, found 42 contacts >" data-id="element-2885">
        <p className="text-sm text-stone-800 leading-relaxed" data-id="element-2886">
          Here are the verified owner and manager-level contacts for your top
          leads:
        </p>
        <div className="space-y-2 mt-3" data-id="element-2887">
          {[{
          name: 'Sarah Chen',
          title: 'Owner & Founder',
          company: 'Salon Luxe SF',
          email: 'sarah@salonluxesf.com'
        }, {
          name: 'Michael Rodriguez',
          title: 'General Manager',
          company: 'Beauty Bar Downtown',
          email: 'm.rodriguez@beautybar.com'
        }].map((contact, i) => <div key={i} className="p-3 rounded-xl border border-stone-200 bg-white shadow-sm flex items-start gap-3" data-id="element-2888">
              <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center shrink-0" data-id="element-2889">
                <span className="text-xs font-bold text-blue-700" data-id="element-2890">
                  {contact.name.split(' ').map(n => n[0]).join('')}
                </span>
              </div>
              <div className="flex-1 min-w-0" data-id="element-2891">
                <div className="text-sm font-bold text-stone-800" data-id="element-2892">
                  {contact.name}
                </div>
                <div className="text-xs text-stone-500" data-id="element-2893">
                  {contact.title} · {contact.company}
                </div>
                <div className="flex items-center gap-1.5 text-xs text-stone-600 mt-1.5" data-id="element-2894">
                  <MailIcon className="w-3 h-3 text-stone-400" data-id="element-2895" />
                  {contact.email}
                </div>
              </div>
              <CheckCircle2Icon className="w-4 h-4 text-emerald-500 shrink-0" data-id="element-2896" />
            </div>)}
        </div>
      </AgentMessage>
    </ChatWindow>;
}
function Step5Illustration() {
  return <ChatWindow data-id="element-2897">
      <UserMessage data-id="element-2898">
        Draft personalized outreach emails for these contacts.
      </UserMessage>
      <AgentMessage actionLine="Generated 28 personalized emails >" data-id="element-2899">
        <p className="text-sm text-stone-800 leading-relaxed" data-id="element-2900">
          I've drafted personalized emails for each lead referencing their
          specific data points. Here's a preview for Sarah Chen:
        </p>
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-4 mt-3" data-id="element-2901">
          <div className="space-y-2 mb-3" data-id="element-2902">
            <div className="flex items-center gap-2 text-sm" data-id="element-2903">
              <span className="font-semibold text-stone-400 w-12" data-id="element-2904">To:</span>
              <span className="text-stone-800" data-id="element-2905">sarah@salonluxesf.com</span>
            </div>
            <div className="flex items-center gap-2 text-sm" data-id="element-2906">
              <span className="font-semibold text-stone-400 w-12" data-id="element-2907">
                Subject:
              </span>
              <span className="text-stone-800" data-id="element-2908">
                Quick question about Salon Luxe SF's scheduling
              </span>
            </div>
          </div>
          <div className="border-t border-stone-100 pt-3 text-sm text-stone-700 leading-relaxed space-y-2" data-id="element-2909">
            <p data-id="element-2910">Hi Sarah,</p>
            <p data-id="element-2911">
              I noticed Salon Luxe SF has grown to 3 locations across San
              Francisco—congrats on the expansion! I'm reaching out because many
              multi-location salons we work with struggle with coordinating
              schedules...
            </p>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-3 p-3 rounded-xl border border-stone-200 bg-white shadow-sm" data-id="element-2912">
          <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0" data-id="element-2913">
            <FileSpreadsheetIcon className="w-5 h-5 text-blue-600" data-id="element-2914" />
          </div>
          <div className="flex-1 min-w-0" data-id="element-2915">
            <div className="text-sm font-semibold text-stone-800 truncate" data-id="element-2916">
              personalized_outreach_emails.xlsx
            </div>
            <div className="text-xs text-stone-500" data-id="element-2917">Spreadsheet · XLSX</div>
          </div>
          <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-stone-200 text-xs font-semibold text-stone-700 hover:bg-stone-50 transition-colors" data-id="element-2918">
            <DatabaseIcon className="w-3.5 h-3.5 text-green-600" data-id="element-2919" />
            Google Drive
          </button>
        </div>
      </AgentMessage>
    </ChatWindow>;
}
const illustrations = [Step1Illustration, Step2Illustration, Step3Illustration, Step4Illustration, Step5Illustration];
export function OpenclawCowork() {
  const [activeStep, setActiveStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const progressRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const ActiveIllustration = illustrations[activeStep];
  const startTimer = useCallback(() => {
    // Clear existing timers
    if (timerRef.current) clearTimeout(timerRef.current);
    if (progressRef.current) clearInterval(progressRef.current);
    setProgress(0);
    // Progress bar updates every 50ms
    const startTime = Date.now();
    progressRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const pct = Math.min(elapsed / STEP_DURATION * 100, 100);
      setProgress(pct);
    }, 50);
    // Auto-advance
    timerRef.current = setTimeout(() => {
      setActiveStep(prev => (prev + 1) % steps.length);
    }, STEP_DURATION);
  }, []);
  useEffect(() => {
    startTimer();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (progressRef.current) clearInterval(progressRef.current);
    };
  }, [activeStep, startTimer]);
  const handleStepClick = (index: number) => {
    setActiveStep(index);
  };
  return <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-24" data-id="element-2920">
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 p-8 md:p-12 lg:p-16 relative overflow-hidden" data-id="element-2921">
        {/* Header */}
        <div className="max-w-3xl mb-16" data-id="element-2922">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-sm font-bold mb-4 border border-blue-100" data-id="element-2923">
            <SparklesIcon className="w-4 h-4" data-id="element-2924" />
            Openclaw for sales
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 tracking-tight" data-id="element-2925">
            One request. Structured workflow.
          </h2>
          <p className="text-lg text-slate-500 leading-relaxed" data-id="element-2926">
            From a simple prompt to personalized outreach, the AI agent handles
            every step of your prospecting workflow.
          </p>
        </div>

        {/* Split Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center" data-id="element-2927">
          {/* Left: Vertical Steps */}
          <div className="lg:col-span-4 relative" data-id="element-2928">
            {/* Connecting Line */}
            <div className="absolute left-[23px] top-8 bottom-8 w-px bg-slate-100" data-id="element-2929" />

            <div className="space-y-2 relative z-10" data-id="element-2930">
              {steps.map((step, index) => {
              const isActive = activeStep === index;
              return <div key={step.id} className="relative" data-id="element-2931">
                    <button onClick={() => handleStepClick(index)} className="w-full text-left flex items-start gap-5 p-2 group cursor-pointer" data-id="element-2932">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 text-sm font-bold transition-all duration-300 ${isActive ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20 ring-4 ring-white' : 'bg-slate-50 text-slate-400 border border-slate-200 group-hover:border-blue-300 group-hover:text-blue-500 ring-4 ring-white'}`} data-id="element-2933">
                        {step.id}
                      </div>

                      <div className="pt-3 pb-6 flex-1 min-w-0" data-id="element-2934">
                        <h4 className={`text-lg font-bold transition-colors duration-300 ${isActive ? 'text-slate-900' : 'text-slate-500 group-hover:text-slate-700'}`} data-id="element-2935">
                          {step.title}
                        </h4>

                        <AnimatePresence initial={false} data-id="element-2936">
                          {isActive && <motion.div initial={{
                        height: 0,
                        opacity: 0,
                        marginTop: 0
                      }} animate={{
                        height: 'auto',
                        opacity: 1,
                        marginTop: 8
                      }} exit={{
                        height: 0,
                        opacity: 0,
                        marginTop: 0
                      }} transition={{
                        duration: 0.3,
                        ease: 'easeInOut'
                      }} className="overflow-hidden" data-id="element-2937">
                              {'description' in step && <p className="text-slate-500 leading-relaxed pr-4" data-id="element-2938">
                                  {step.description}
                                </p>}
                              {'highlights' in step && <ul className="space-y-1.5 pr-4" data-id="element-2939">
                                  {(step as any).highlights.map((h: string, hi: number) => <li key={hi} className="flex items-start gap-2 text-slate-500 text-sm leading-relaxed" data-id="element-2940">
                                        <span className="w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0 mt-1.5" data-id="element-2941" />
                                        {h}
                                      </li>)}
                                </ul>}
                              {/* Progress bar */}
                              <div className="mt-4 h-1 w-full bg-slate-100 rounded-full overflow-hidden" data-id="element-2942">
                                <div className="h-full bg-blue-500 rounded-full transition-none" style={{
                            width: `${progress}%`
                          }} data-id="element-2943" />
                              </div>
                            </motion.div>}
                        </AnimatePresence>
                      </div>
                    </button>
                  </div>;
            })}
            </div>
          </div>

          {/* Right: Illustration Area */}
          <div className="lg:col-span-8" data-id="element-2944">
            <div className="relative rounded-3xl bg-slate-50 border border-slate-100 p-6 md:p-10 lg:p-12 overflow-hidden min-h-[500px] flex items-center justify-center" data-id="element-2945">
              {/* Subtle blue glow */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-blue-400/[0.06] blur-[100px] rounded-full pointer-events-none" data-id="element-2946" />

              <div className="relative z-10 w-full" data-id="element-2947">
                <AnimatePresence mode="wait" data-id="element-2948">
                  <motion.div key={activeStep} initial={{
                  opacity: 0,
                  y: 20,
                  scale: 0.97
                }} animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  y: -20,
                  scale: 0.97
                }} transition={{
                  duration: 0.4,
                  ease: [0.25, 0.46, 0.45, 0.94]
                }} data-id="element-2949">
                    <ActiveIllustration data-id="element-2950" />
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}